/**
 * API Client Configuration
 * Sistema de Renta de Carros - Tattersall
 */

import { useUserStore } from '@/store'

const API_BASE_URL =
	process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api'

interface FetchOptions extends RequestInit {
	token?: string
}

/**
 * Custom fetcher for SWR with authentication support
 */
export async function fetcher<T>(
	url: string,
	options?: FetchOptions,
): Promise<T> {
	const { token: providedToken, ...fetchOptions } = options || {}

	// Get token from store if not provided
	const token = providedToken || useUserStore.getState().token

	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
	}

	if (token) {
		headers['Authorization'] = `Bearer ${token}`
	}

	// Merge with provided headers
	if (fetchOptions.headers) {
		Object.assign(headers, fetchOptions.headers)
	}

	// Determine full URL
	const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`

	const response = await fetch(fullUrl, {
		...fetchOptions,
		headers,
	})

	if (!response.ok) {
		const errorBody = await response.json().catch(() => null)
		if (errorBody) {
			console.error(`API ${response.status} error:`, JSON.stringify(errorBody))
		}
		const message =
			errorBody?.message || errorBody?.title || errorBody?.errors
				? JSON.stringify(errorBody?.errors ?? errorBody)
				: `API Error ${response.status}`
		throw new Error(
			typeof message === 'string' ? message : JSON.stringify(message),
		)
	}

	return response.json()
}

/**
 * API client methods
 */
export const api = {
	get: <T>(url: string, options?: FetchOptions) =>
		fetcher<T>(url, { ...options, method: 'GET' }),

	post: <T>(url: string, data?: unknown, options?: FetchOptions) =>
		fetcher<T>(url, {
			...options,
			method: 'POST',
			body: JSON.stringify(data),
		}),

	put: <T>(url: string, data?: unknown, options?: FetchOptions) =>
		fetcher<T>(url, {
			...options,
			method: 'PUT',
			body: JSON.stringify(data),
		}),

	patch: <T>(url: string, data?: unknown, options?: FetchOptions) =>
		fetcher<T>(url, {
			...options,
			method: 'PATCH',
			body: JSON.stringify(data),
		}),

	delete: <T>(url: string, options?: FetchOptions) =>
		fetcher<T>(url, { ...options, method: 'DELETE' }),
}

export default api
